import { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";

/**
 * Hook to fetch CMS collections with fallback support
 * Listens to API_BASE_URL/public/v2/content/:collectionName
 */
export function useCmsCollection(collectionName, options = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { sort = "sort_order", order = "asc", limit = 100 } = options;

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams();
        if (sort) queryParams.set("sort", sort);
        if (order) queryParams.set("order", order);
        if (limit) queryParams.set("limit", String(limit));

        const res = await fetch(
          `${API_BASE_URL}/public/v2/content/${collectionName}?${queryParams.toString()}`,
          { headers: { Accept: "application/json" } }
        );

        if (!res.ok) {
          // Try fallback plain endpoint
          const plainRes = await fetch(`${API_BASE_URL}/public/content/${collectionName}`);
          if (plainRes.ok) {
            const data = await plainRes.json();
            if (isMounted) {
              setItems(Array.isArray(data) ? data : []);
              setTotal(Array.isArray(data) ? data.length : 0);
              setLoading(false);
            }
            return;
          }
          throw new Error(`Failed to load ${collectionName}: ${res.status}`);
        }

        const json = await res.json();
        if (isMounted) {
          const list = json.items || [];
          setItems(list);
          setTotal(json.total || list.length);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          console.warn(`CMS fetch error for ${collectionName}:`, err);
          setError(err.message);
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, [collectionName, sort, order, limit]);

  return { items, total, loading, error };
}
